import React, {
    Fragment,
    Suspense,
    Dispatch,
    SetStateAction,
    useCallback,
    useState,
    useEffect,
    useReducer,
    lazy,
    memo,
} from 'react';
import { ITourMainProps } from '../../components/tour/interface';
import placeholderData from '../../components/tour/placeholderData';
import { ISiteObjectDto } from '@monofe/apiservice/model';
import { ITourControllerProps, ITourStateActionModifier, ITourType } from './interface';
import LoadingView from '../../components/common/Loading';
import { injectCacheController, injectLog } from '../../interface/util';
import type IMapping from './mapping';
import { getProjectOverrides } from '../../interface/overrides';
import { AnimatePresence } from 'framer-motion';

const MainView = lazy(() => import('../../components/tour'));
const loadMapper = (): Promise<typeof IMapping> => {
    return new Promise(function (resolve, reject) {
        import('./mapping')
            .then(controller => resolve(controller.default))
            .catch(error => reject(error));
    });
};

interface ITourController extends ITourMainProps {
    type: ITourType;
    isLoading: boolean;
}

export interface ITourControllerActionModifier {
    (model: ITourController): ITourController;
}

/* Same as useState, except 3 return-value allow for partial state updates */
function ReactUseStateWithUpdate<S>(
    initialState: S | (() => S),
): [S, Dispatch<SetStateAction<S>>, Dispatch<Partial<S>>] {
    const [getVal, setVal] = useState<S>(initialState);
    const updateVal = useCallback(
        (partial: Partial<S>) => setVal(sPrev => ({ ...sPrev, ...partial })),
        [],
    );
    return [getVal, setVal, updateVal];
}

function _StateReducer(
    state: ITourController,
    action: ITourControllerActionModifier,
): ITourController {
    const newState = action(state);
    return newState;
}

function View(props: ITourControllerProps): JSX.Element {
    const log = injectLog();

    const [model, modelDispatcher] = useReducer(_StateReducer, {
        ...placeholderData(),
        type: ITourType.List,
        isLoading: true,
    });

    const onSiteObject = useCallback(
        (siteObject: ISiteObjectDto) => {
            props.didRequestSiteObject(siteObject.id);
        },
        [props.didRequestSiteObject],
    );
    const onFloorPlan = props.didRequestFloorPlan;
    const onLanguageSelect = props.didRequestLanguageSelect;
    const onTourSelect = props.didRequestTourSelect;
    const onBack = props.didRequestBack;
    const reducer = (modifier: ITourStateActionModifier) => {
        modelDispatcher((modelState: ITourController) => {
            return {
                type: modelState.type,
                isLoading: modelState.isLoading,
                /* apply reducer & pass result to project-overrides before applying */
                ...getProjectOverrides<ITourMainProps>('ITourMainProps', modifier(modelState)),
            };
        });
    };

    useEffect(() => {
        log.info(`New route: ${JSON.stringify(props.route)}`);
        loadMapper()
            .then(function (mapper) {
                return mapper(
                    props.route,
                    reducer,
                    injectCacheController().getAssociatedCache(props) ||
                        injectCacheController().getGlobalCache(),
                    onSiteObject,
                    onFloorPlan,
                    onLanguageSelect,
                    onTourSelect,
                    onBack,
                );
            })
            .then(([modelResult, type]) => {
                reducer(modelIn => ({
                    ...modelIn,
                    ...modelResult,
                    type,
                    isLoading: false,
                }));
            })
            .catch(error => {
                log.error(`Failed to map/update tour controller. Error:${JSON.stringify(error)}`);
                props.didRequestBack();
            });
    }, [props.route]);

    useEffect(() => {
        document.title = model.topBar.title.text;
    }, [model.topBar.title.text]);

    useEffect(() => {
        if (!model.isLoading) {
            log.info('Showing Tour controller view');
        }
    }, [model.isLoading]);

    return (
        <AnimatePresence>
            {model.isLoading ? (
                <LoadingView
                    styleOverrides={{
                        transitionDuration: '0.3s',
                        opacity: 1,
                        pointerEvents: 'auto',
                    }}
                />
            ) : (
                <Suspense fallback={null}>
                    <MainView key="Tour" type={model.type} model={model} />
                </Suspense>
            )}
        </AnimatePresence>
    );
}

View.displayName = 'Tour-render-View';

const propsEqual = (prev: ITourControllerProps, next: ITourControllerProps) => {
    const match = JSON.stringify(prev.route) === JSON.stringify(next.route);
    return match;
};

export default memo(View, propsEqual) as typeof View;
